import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UIButton, Icon } from '../primitives';
import { shareUrl } from '../../platform';
import useConsentGuard from '../../hooks/useConsentGuard';

/**
 * ReferralBlock — «Приглашено друзей» карточка на Profile (Figma 318:5927).
 *
 * Geometry (identical to PlanBlock & CreditsBlock):
 *   FILL × HUG, cornerRadius 24, padding 24, itemSpacing 16,
 *   diagonal glass surface + 1px gradient stroke.
 *
 * Layout (top → bottom):
 *   1. title (FILL × HUG, gap=8):
 *        - «Приглашено друзей» — Raleway Bold 16, text-secondary
 *        - count — Raleway Bold 24 (NOT 32), text-primary
 *   2. share (FILL × HUG, gap=12):
 *        - url-row (HORIZONTAL, FILL × HUG, h=40, gap=8):
 *            * «input» pill — 40px tall, radius 12, fill glass-8,
 *              padding 12/16, text Inter 14/600, UNDERLINED, white;
 *            * copy button — 40×40 square, uiButton secondary md icon-only
 *              (leftIcon=copy, no label)
 *        - share button — uiButton secondary md FILL × 40,
 *          leftIcon=telegram (telegram-blue #24A1DE, not white),
 *          label «Поделиться ссылкой»
 *   3. helper text — Inter 14/500, text-secondary, LEFT-aligned (not center).
 *
 * Props:
 *  - referralUrl   (string | null)
 *  - referralCount (number)  — количество приглашённых
 */
export default function ReferralBlock({
  referralUrl = null,
  referralCount = 0,
  restricted = false,
}) {
  const { t } = useTranslation();
  const { requireConsent } = useConsentGuard();
  const [copied, setCopied] = useState(false);
  const displayUrl = referralUrl;

  const performCopy = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = referralUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        /* clipboard unavailable */
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopy = () => {
    if (!referralUrl) return;
    if (restricted) {
      requireConsent(performCopy, 'generate');
      return;
    }
    performCopy();
  };

  const performShare = async () => {
    if (!referralUrl) return;

    try {
      if (shareUrl(referralUrl)) {
        return;
      }
    } catch {
      /* fall through */
    }

    if (navigator.share) {
      try {
        await navigator.share({ url: referralUrl });
        return;
      } catch {
        /* cancelled or unsupported */
      }
    }

    performCopy();
  };

  const handleShare = () => {
    if (!referralUrl) return;
    if (restricted) {
      requireConsent(performShare, 'generate');
      return;
    }
    performShare();
  };

  return (
    <div
      className="glass-card rounded-home-24 p-6 flex flex-col gap-4"
      data-testid="referral-block"
      aria-disabled={restricted || undefined}
    >
      {/* title: label + count */}
      <div className="flex flex-col gap-2">
        <span className="font-sans font-bold text-h2 text-text-secondary">
          {t('profile.friendsInvited')}
        </span>
        <span
          className="font-sans font-bold text-[24px] leading-none text-text-primary"
          data-testid="referral-count"
        >
          {referralCount}
        </span>
      </div>

      {/* share section: url-row + share button */}
      <div className="flex flex-col gap-3">
        {displayUrl && (
          <div className="flex items-center gap-2">
            {/* url input pill */}
            <div className="flex h-10 min-w-0 flex-1 items-center rounded-home-12 bg-glass-8 px-4">
              <span
                className="block min-w-0 truncate whitespace-nowrap font-sans text-[14px] font-semibold leading-[18px] text-text-primary underline underline-offset-2"
                title={referralUrl}
                data-testid="referral-link-display"
              >
                {displayUrl}
              </span>
            </div>
            {/* copy button — 40×40 secondary md icon-only */}
            <button
              type="button"
              onClick={handleCopy}
              disabled={!referralUrl}
              aria-label={copied ? t('profile.copied') : t('profile.copyLink')}
              className={[
                'shrink-0 inline-flex items-center justify-center',
                'w-10 h-10 rounded-home-20',
                'bg-glass-16 backdrop-blur-secondary-btn',
                'text-text-primary active:opacity-70 transition-opacity',
              ].join(' ')}
            >
              <Icon
                name={copied ? 'check' : 'copy'}
                size={16}
                className={copied ? 'text-accent-orange-start' : 'text-text-primary'}
              />
            </button>
          </div>
        )}

        <UIButton
          type="secondary"
          size="md"
          leftIcon="telegram"
          onClick={handleShare}
          disabled={!referralUrl}
          aria-label={t('profile.shareLink')}
          className="w-full text-brand-telegram-blue"
        >
          <span className="text-text-primary">{t('profile.shareLink')}</span>
        </UIButton>
      </div>

      {/* helper text — Inter 14/500 left-aligned (no text-center) */}
      <span className="font-sans font-medium text-[14px] leading-none text-text-secondary">
        {t('profile.referralHelper')}
      </span>
    </div>
  );
}
